import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { User, Lock, Bell, Shield } from 'lucide-react';
import { toast } from 'sonner';
import ProfileSection from './settings/ProfileSection';
import PasswordSection from './settings/PasswordSection';
import NotificationsSection from './settings/NotificationsSection';
import SecuritySection from './settings/SecuritySection';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserProfileSettings = ({ user, onUserUpdate }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // True after a change-password request has been submitted but not yet
  // confirmed via the email link. Drives the amber "Check your email" notice
  // inside PasswordSection so the founder knows the change is staged, not
  // yet applied. Cleared on tab switch or component unmount.
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);

  const [profileData, setProfileData] = useState({
    name: user?.name || '', email: user?.email || '',
    country: user?.country || '', billing_country: user?.billing_country || ''
  });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: user?.email_notifications ?? true,
    introduction_alerts: user?.introduction_alerts ?? true,
    portfolio_updates: user?.portfolio_updates ?? true,
    security_alerts: user?.security_alerts ?? true,
    maturity_reminders: user?.maturity_notifications_enabled ?? true
  });
  const [securitySettings, setSecuritySettings] = useState({
    two_factor_enabled: user?.two_factor_enabled ?? false,
    login_alerts: user?.login_alerts ?? true
  });

  useEffect(() => {
    if (activeSection === 'security') fetchLoginHistory();
  }, [activeSection]);

  // Deep-link support: another part of the app (e.g. the "Please strengthen
  // your password" nudge at the top of Settings) can dispatch
  //   window.dispatchEvent(new CustomEvent('mif:open-account-section',
  //     { detail: 'password' }))
  // and this hook opens the requested sub-tab + scrolls it into view.
  useEffect(() => {
    const handler = (event) => {
      const id = event?.detail;
      if (!id) return;
      setActiveSection(id);
      // Wait a tick so the section renders before we scroll to it.
      requestAnimationFrame(() => {
        const target = document.getElementById(`change-password-section`);
        if (id === 'password' && target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    };
    window.addEventListener('mif:open-account-section', handler);
    return () => window.removeEventListener('mif:open-account-section', handler);
  }, []);

  const getHeaders = (withJson = false) => {
    const token = localStorage.getItem('auth_token');
    const h = token ? { 'Authorization': `Bearer ${token}` } : {};
    if (withJson) h['Content-Type'] = 'application/json';
    return h;
  };

  const fetchLoginHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login-history?limit=10`, { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); setLoginHistory(data.login_history || []); }
    } catch (e) { logger.error('Error fetching login history:', e); }
    finally { setLoadingHistory(false); }
  };

  const apiCall = async (url, method, body) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}${url}`, { method, headers: getHeaders(true), body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || `Request failed`); }
      return await res.json();
    } finally { setIsLoading(false); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const updated = await apiCall('/api/users/profile', 'PUT', profileData);
      toast.success('Profile updated successfully!');
      if (onUserUpdate) onUserUpdate(updated);
    } catch (err) { toast.error(err.message || 'Failed to update profile'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error('New passwords do not match'); return; }
    const pw = passwordData.new_password;
    if (pw.length < 8 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw) || !/[!@#$%^&*(),.?":{}|<>]/.test(pw)) {
      toast.error('Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character (like !@#$%).');
      return;
    }
    try {
      // Backend stages the change in a pending_password_changes collection
      // and returns { requires_confirmation: true } — the password is only
      // actually swapped after the email link is clicked. So the toast must
      // reflect that this is step 1 of 2, not a completed change. (apiCall
      // handles the isLoading toggle already.)
      const resp = await apiCall('/api/users/change-password', 'POST', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      if (resp?.requires_confirmation) {
        toast.success('Almost there — check your email to confirm the change.');
        setAwaitingConfirmation(true);
      } else {
        toast.success('Password changed successfully!');
        setAwaitingConfirmation(false);
      }
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.message || 'Failed to change password'); }
  };

  const handleUpdateNotifications = async (e) => {
    e.preventDefault();
    try { await apiCall('/api/users/notifications', 'PUT', notificationPrefs); toast.success('Notification preferences updated!'); }
    catch (err) { logger.error(err); toast.error('Failed to update notification preferences'); }
  };

  const handleUpdateSecurity = async (e) => {
    e.preventDefault();
    try { await apiCall('/api/users/security', 'PUT', securitySettings); toast.success('Security settings updated!'); }
    catch (err) { logger.error(err); toast.error('Failed to update security settings'); }
  };

  const sections = [
    { id: 'profile', label: 'Profile Information', icon: User },
    { id: 'password', label: 'Change Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {sections.map(({ id, label, icon: Icon }) => (
          <Button key={id} variant={activeSection === id ? 'default' : 'outline-solid'} onClick={() => setActiveSection(id)} className="flex items-center space-x-2" data-testid={`profile-subnav-${id}`}>
            <Icon className="w-4 h-4" /><span>{label}</span>
          </Button>
        ))}
      </div>

      {activeSection === 'profile' && (
        <ProfileSection profileData={profileData} setProfileData={setProfileData} onSubmit={handleUpdateProfile} isLoading={isLoading} />
      )}
      {activeSection === 'password' && (
        <PasswordSection passwordData={passwordData} setPasswordData={setPasswordData} showPasswords={showPasswords} setShowPasswords={setShowPasswords} onSubmit={handleChangePassword} isLoading={isLoading} awaitingConfirmation={awaitingConfirmation} />
      )}
      {activeSection === 'notifications' && (
        <NotificationsSection notificationPrefs={notificationPrefs} setNotificationPrefs={setNotificationPrefs} onSubmit={handleUpdateNotifications} isLoading={isLoading} />
      )}
      {activeSection === 'security' && (
        <SecuritySection securitySettings={securitySettings} setSecuritySettings={setSecuritySettings} onSubmit={handleUpdateSecurity} isLoading={isLoading} loginHistory={loginHistory} loadingHistory={loadingHistory} />
      )}
    </div>
  );
};

export default UserProfileSettings;
